import React, { useMemo } from 'react';

export default function ShortTickers({ tickers, shortSymbols }) {
  const shortTickers = useMemo(() => {
    if (!shortSymbols || shortSymbols.size === 0) return [];

    const list = [];
    for (const sym of shortSymbols) {
      const live = tickers[sym];
      if (live) {
        list.push({ sym, price: live.price, changePercent: live.changePercent, hasData: true });
      } else {
        list.push({ sym, price: null, changePercent: null, hasData: false });
      }
    }

    // Tickers with live data first (sorted by change% desc), then no-data symbols alphabetically
    list.sort((a, b) => {
      if (a.hasData && !b.hasData) return -1;
      if (!a.hasData && b.hasData) return 1;
      if (a.hasData && b.hasData) return b.changePercent - a.changePercent;
      return a.sym.localeCompare(b.sym);
    });

    return list;
  }, [tickers, shortSymbols]);

  const liveCount = useMemo(() => {
    return shortTickers.filter((t) => t.hasData).length;
  }, [shortTickers]);

  const openTradingView = (symbol) => {
    window.open(
      `https://www.tradingview.com/chart/?symbol=${symbol}&interval=1`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  if (shortSymbols.size === 0) {
    return (
      <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">Short Tickers</h2>
        </div>
        <div className="px-4 sm:px-6 py-12 text-center">
          <p className="text-sm text-gray-500">Loading short tickers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
      <div className="px-4 sm:px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Short Tickers</h2>
          <span className="text-xs text-gray-400">
            {liveCount} / {shortSymbols.size} tickers live
          </span>
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
              <th className="px-4 sm:px-6 py-3 bg-gray-50">Ticker</th>
              <th className="px-4 sm:px-6 py-3 bg-gray-50 text-right">Price</th>
              <th className="px-4 sm:px-6 py-3 bg-gray-50 text-right">Change %</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {shortTickers.map((ticker) => {
              if (!ticker.hasData) {
                return (
                  <tr
                    key={ticker.sym}
                    onClick={() => openTradingView(ticker.sym)}
                    className="hover:bg-violet-50 active:bg-violet-100 cursor-pointer transition-colors"
                  >
                    <td className="px-4 sm:px-6 py-3.5">
                      <span className="text-sm font-semibold text-gray-900">{ticker.sym}</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-right">
                      <span className="text-sm text-gray-400 tabular-nums">—</span>
                    </td>
                    <td className="px-4 sm:px-6 py-3.5 text-right">
                      <span className="text-sm text-gray-400 tabular-nums">—</span>
                    </td>
                  </tr>
                );
              }
              const isPositive = ticker.changePercent >= 0;
              return (
                <tr
                  key={ticker.sym}
                  onClick={() => openTradingView(ticker.sym)}
                  className="hover:bg-violet-50 active:bg-violet-100 cursor-pointer transition-colors"
                >
                  <td className="px-4 sm:px-6 py-3.5">
                    <span className="text-sm font-semibold text-gray-900">{ticker.sym}</span>
                  </td>
                  <td className="px-4 sm:px-6 py-3.5 text-right">
                    <span className="text-sm text-gray-700 font-medium tabular-nums">
                      ${ticker.price.toFixed(2)}
                    </span>
                  </td>
                  <td className="px-4 sm:px-6 py-3.5 text-right">
                    <span className={`inline-flex items-center gap-1 text-sm font-semibold tabular-nums ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                      {isPositive ? (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 17a.75.75 0 01-.75-.75V5.612L5.29 9.77a.75.75 0 01-1.08-1.04l5.25-5.5a.75.75 0 011.08 0l5.25 5.5a.75.75 0 11-1.08 1.04l-3.96-4.158V16.25A.75.75 0 0110 17z" clipRule="evenodd" />
                        </svg>
                      ) : (
                        <svg className="w-3.5 h-3.5" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M10 3a.75.75 0 01.75.75v10.638l3.96-4.158a.75.75 0 111.08 1.04l-5.25 5.5a.75.75 0 01-1.08 0l-5.25-5.5a.75.75 0 111.08-1.04l3.96 4.158V3.75A.75.75 0 0110 3z" clipRule="evenodd" />
                        </svg>
                      )}
                      {isPositive ? '+' : ''}{ticker.changePercent.toFixed(2)}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
